import { useTheme } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcContainer, MprcRequestErrorReport } from "components";
import { FeedChannelHeaderResponse, FeedType, KnowledgeFeedOrder, QuickFilterDto } from "generated/CoreApi_v1";
import { useReferredState } from "hooks/useReferredState";
import { useWindowSize } from "hooks/useWindowSize";
import { useInsideLayout } from "layouts/common/inside/InsideLayoutContext";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { ComplexHeaderLoading } from "views/knowledge/components/ComplexInfoHeader";
import { FeedContainer } from "views/knowledge/feed/components/FeedContainer";
import { IFeedFilterOptions } from "views/knowledge/feed/components/FeedFilter";
import { getFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { FeedManager, FeedManagerHandle } from "views/knowledge/feed/components/FeedManager";
import { FeedChannelFilter } from "views/knowledge/feed/FeedChannel/FeedChannelFilter";
import { FeedChannelHeader } from "views/knowledge/feed/FeedChannel/FeedChannelHeader";
import { FeedChannelRightPanel } from "views/knowledge/feed/FeedChannel/FeedChannelRightPanel";
import { FeedCustomEventProps, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { OutsideRightPanel } from "views/knowledge/publication/pages/components/OutsideRightPanel";

interface IProps {
    isHome?: boolean;
}

/**
 * Feed do Canal.
 * @param props
 */
export const FeedChannel: React.FC<IProps> = ({ isHome }) => {
    const { id } = useParams();
    const { t } = useTranslation("knowledge");
    const layout = useInsideLayout();

    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const { width } = useWindowSize({ wait: 50 });

    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const [count, setCount] = useState<number | undefined | null>(undefined);
    const [viewError, setViewError] = useState<any>();
    const [channelId, setChannelId, refChannelId] = useReferredState<string | undefined>(undefined);
    const [channel, setChannel] = useState<FeedChannelHeaderResponse>();
    const [loading, setLoading] = useState<boolean>(true);
    const [prevPub, setPrevPub] = useState<string | null>(location.state?.publicationId ?? null);
    const [expandedAdvFilter, setExpandedAdvFilter] = useState<boolean>(location.state?.expanded ?? false);
    const prevExpandedAdvFilter = useRef<boolean>(false);
    const [showPanel, setShowPanel] = useState<boolean>(true);
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);

    const viewFilter = useRef<IFeedFilterOptions>(undefined!);

    ///
    const handleAdvFilterChange = useCallback((filter: IFeedFilterOptions) => {
        viewFilter.current = filter;
        setCount(null);
        setPrevPub(null);
    }, [id]);

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        const filters = getFilter(viewFilter.current);

        var result = await api.v1.knowledgeFeed.getFeedChannel({
            channelId: channel!.channelId,
            startIndex,
            endIndex,
            filters,
            startPublicationId: location.state?.publicationId ?? null,
            order: viewFilter.current.order,
            searchText: viewFilter.current.searchText ?? null,
        });
        setCount(result.data.totalRows);
        return result.data.items;
    };

    ///
    const load = () => {
        setLoading(true);
        setViewError(null);

        if (channelId) {
            api.v1.knowledgeFeed.getFeedChannelHeader({ channelId })
                .then((result) => {
                    setChannel(result.data);
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    ///
    const handleReset = () => {
        if (channelId) {
            load();
        }
    };

    ///
    const handleNewItem = (e: FeedCustomEventProps) => {
        if (e.detail == refChannelId.current && viewFilter.current.searchText == "" && viewFilter.current.advFilters.length == 0 && !feedRef.current.scrolled()) {
            handleAdvFilterChange({ ...viewFilter.current, order: KnowledgeFeedOrder.Latest });
        }
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (channelId) load();
    }, [channelId]);

    ///
    useEffect(() => {
        feedRef.current?.reset();
    }, [channel]);

    ///
    useEffect(() => {
        if (count == null && prevPub == null) {
            navigate("", { replace: true, state: { filter: viewFilter.current, expanded: expandedAdvFilter } });
        }
    }, [count]);

    ///
    useEffect(() => {
        if (prevPub != location.state?.publicationId) {
            setPrevPub(null);
        }
        else if (channelId && channelId == id && prevExpandedAdvFilter.current == expandedAdvFilter) {
            setCount(null);
            feedRef.current?.reset();
        }
    }, [location.state]);

    ///
    useEffect(() => {
        prevExpandedAdvFilter.current = expandedAdvFilter;
    }, [expandedAdvFilter]);

    ///
    useEffect(() => {
        layout.changeSubHeaderText(t("Feed.Channel.ChannelText"));
        const urlParams = new URLSearchParams(location.search);
        const initialOrderBy = KnowledgeFeedOrder[urlParams.get("o") as keyof typeof KnowledgeFeedOrder];
        // Pelo acesso rápido, a ordem deve ser inicializada como "Mais Recente".
        const order = initialOrderBy ?? (isHome ? KnowledgeFeedOrder.Latest : KnowledgeFeedOrder.MostRelevant);

        setCount(null);
        setChannelId(id);
        setPrevPub(location.state?.publicationId ?? null);
        viewFilter.current = location.state?.filter
            ?? {
                searchText: "",
                advFilters: [],
                order
            } as IFeedFilterOptions;
    }, [document.location.pathname]);

    ///
    useEffect(() => {
        layout.changeSubHeaderText(t("Feed.Channel.ChannelText"));

        subscribe("ChannelNewPublication", handleNewItem);
        return () => {
            unsubscribe("ChannelNewPublication", handleNewItem);
        };
    }, []);

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            setOpenRightPanel(false);
    }, [width]);

    const showRightPanel = useMemo(() => {
        return width < theme.breakpoints.values["lg"];
    }, [width]);


    ////////////////////////////////////////////////////////////////////////////////

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    return viewError
        ? <MprcRequestErrorReport
            error={viewError}
            onRetry={() => load()}
        />
        : loading
            ? <ComplexHeaderLoading defaultThumbnail={AppImages.KnowledgeChannelThumbnail} />
            : (
                <>
                    <MprcContainer margin="dense">
                        <FeedChannelHeader
                            channel={channel ?? null}
                            disableGoback={isHome === true}
                            onReset={handleReset}
                            onRigthPanelButtonClick={showRightPanel ? () => setOpenRightPanel(state => !state) : undefined}
                        />
                    </MprcContainer>

                    <MprcContainer margin="normal">
                        <FeedChannelFilter
                            channel={channel!}
                            count={count ?? undefined}
                            filter={viewFilter.current}
                            expandedAdvFilter={expandedAdvFilter}
                            onFilterChange={handleAdvFilterChange}
                            onError={(error) => setViewError(error)}
                            onGetQuickFilterOptions={() =>
                                new Promise<QuickFilterDto[]>((resolve, reject) => {
                                    api.v1.knowledgeFeed.getQuickFilterFeedChannel({ channelId: channelId! })
                                        .then(result => resolve(result.data))
                                        .catch(e => reject(e));
                                })
                            }
                            onSelectedExpandedAdvFilter={(selected) => {
                                setExpandedAdvFilter(selected);
                                navigate("", { replace: true, state: { expanded: selected } });
                            }}
                        />
                    </MprcContainer>

                    <FeedContainer
                        feed={
                            <>
                                <FeedManager
                                    feedType={FeedType.Channel}
                                    otherInfo={{ feedType: FeedType.Channel }}
                                    onGetData={onGetData}
                                    ref={feedRef}
                                    useInfiniteLoading
                                    previousPublicationId={prevPub}
                                    onGobackTop={() => {
                                        setCount(null);
                                        setPrevPub(null);
                                    }}
                                    onError={(error) => setViewError(error)}
                                />
                            </>
                        }
                        moreInfo={
                            channelId && count && count > 0 && showPanel
                                ? <FeedChannelRightPanel
                                    key="FeedChannelRightPanel"
                                    channelId={channelId}
                                    renderMode="default"
                                    onEmptyPanel={() => setShowPanel(false)}
                                />
                                : undefined
                        }
                        marginRightPanel={0}
                    />

                    <OutsideRightPanel
                        open={openRightPanel}
                        onClose={() => setOpenRightPanel(false)}
                        content={
                            <FeedChannelRightPanel
                                key="FeedChannelRightPanel"
                                channelId={channelId!}
                                renderMode="drawer"
                                onEmptyPanel={() => setShowPanel(false)}
                            />
                        }
                    />
                </>
            );
};
